/**
 * RTWO Cryptography & Identity Engine — Key Storage Policy Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { KeyStorageType, PolicyId, RTWOKeyStoragePolicy } from '../domain-models';
import type { IKeyStoragePolicyService } from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

let policyCounter = 0;

function generatePolicyId(): string {
  policyCounter += 1;
  return `policy-${policyCounter}`;
}

export class KeyStoragePolicyService extends BaseCryptoService implements IKeyStoragePolicyService {
  readonly serviceId = CRYPTO_SERVICE_IDS.KEY_STORAGE_POLICY;
  readonly serviceName = 'Key Storage Policy Service';
  readonly description =
    'Manages key storage policies. Defines security requirements for key storage.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation', 'R7 Enterprise Services'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories
  ) {
    super(context);
  }

  createPolicy(
    storageType: KeyStorageType,
    options: {
      encryptionRequired: boolean;
      rotationRequired: boolean;
      backupRequired: boolean;
      recoveryAllowed: boolean;
    }
  ): RTWOKeyStoragePolicy {
    const policy: RTWOKeyStoragePolicy = {
      policyId: generatePolicyId(),
      storageType,
      ...options,
      status: storageType === 'not_configured' ? 'not_configured' : 'defined',
    };

    this.repositories.policies.save(policy);
    return policy;
  }

  getPolicy(policyId: PolicyId): RTWOKeyStoragePolicy | null {
    return this.repositories.policies.findById(policyId);
  }

  getAllPolicies(): RTWOKeyStoragePolicy[] {
    return this.repositories.policies.findAll();
  }

  getDefaultPolicy(_storageType: KeyStorageType): RTWOKeyStoragePolicy {
    return {
      policyId: 'default-not-configured',
      storageType: 'not_configured',
      encryptionRequired: true,
      rotationRequired: true,
      backupRequired: true,
      recoveryAllowed: false,
      status: 'not_configured',
    };
  }
}
